import { Tooltip } from "@client/components/InfoTip";
import { AdoptionIcon, LedgerIcon, PackagesIcon, SettingsIcon } from "@client/components/icons";

/** The four groups of inputs, each behind one button on the rail. */
export type EditorPanelKey = "settings" | "packages" | "lines" | "adoption";

export interface PanelCounts {
  packages: number;
  lines: number;
  years: number;
}

const ITEMS: {
  key: EditorPanelKey;
  label: string;
  Icon: (props: { className?: string }) => JSX.Element;
  count?: (counts: PanelCounts) => number;
}[] = [
  { key: "settings", label: "Scenario settings", Icon: SettingsIcon },
  { key: "packages", label: "Packages", Icon: PackagesIcon, count: (c) => c.packages },
  { key: "lines", label: "Costs & revenues", Icon: LedgerIcon, count: (c) => c.lines },
  { key: "adoption", label: "Adoption & churn", Icon: AdoptionIcon, count: (c) => c.years },
];

/**
 * The inputs, reduced to four buttons.
 *
 * Every input used to be on screen at once in a 460px column, which cost the
 * charts a third of the page and the reader several screens of scrolling. The
 * inputs have not moved in the model — only in the layout — so the rail is the
 * whole navigation for them, and the count on each button is what is lost by
 * hiding the contents: how many packages, how many lines, how many years.
 *
 * Vertical and sticky from `lg`, a compact row of the same icons below it.
 * Icons only at every width: the label is one hover or one focus away through
 * the same tooltip the input hints use, and spelling the labels out on a narrow
 * screen pushed the rail onto a second row — two rows of chrome above the charts
 * this layout exists to make bigger.
 */
export function EditorToolbar({
  open,
  onOpen,
  counts,
}: {
  open: EditorPanelKey | null;
  onOpen: (key: EditorPanelKey) => void;
  counts: PanelCounts;
}) {
  return (
    <nav
      aria-label="Scenario inputs"
      // Sticky only from `lg`, and below both the shell's header and the KPI
      // strip — the two things already stuck up there. Below `lg` the strip
      // wraps to two or three rows and its height stops being knowable, so the
      // rail scrolls with the page instead of guessing.
      className="island-shell z-10 flex shrink-0 flex-row gap-1 p-2 lg:sticky lg:top-[calc(var(--app-header-h)+var(--kpi-h)+1rem)] lg:w-16 lg:flex-col"
    >
      {ITEMS.map(({ key, label, Icon, count }) => {
        const active = open === key;
        const badge = count?.(counts);
        return (
          // No `id` on the tooltip: the button's `aria-label` already says the
          // same words, and a description that repeats the name reads it twice.
          <Tooltip key={key} content={label} className="relative flex flex-1 lg:flex-none">
            <button
              type="button"
              aria-label={label}
              aria-haspopup="dialog"
              aria-expanded={active}
              onClick={() => onOpen(key)}
              className={`flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors lg:flex-col lg:gap-1 lg:px-1 ${
                active
                  ? "bg-azure-deep text-on-accent"
                  : "text-ink-soft hover:bg-link-bg-hover hover:text-ink"
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {badge !== undefined && (
                <span
                  aria-hidden="true"
                  className={`tabular rounded-full px-1.5 text-[11px] font-bold ${
                    active ? "bg-black/20 text-on-accent" : "bg-chip-bg text-ink-soft"
                  }`}
                >
                  {badge}
                </span>
              )}
            </button>
          </Tooltip>
        );
      })}
    </nav>
  );
}
